import { supabase } from './supabase';
import { applyTransform } from '../stores/mappingStore';
import { applyMappedValue, applyTextFit } from './mappingValueApplicator';
import { buildEavPivotKey, isEavMapping } from './mappingResolver';
import type { CanvasElement, SchoolBrand, TextElement } from '../types/index';
import type { EnhancedFieldMapping } from '../types/mapping';
import type { ResolvedElement, AppliedMapping } from '../types/preview';

const COLOR_NOT_SET = '#000000';

export async function fetchBrandsForExport(
  schoolIds?: string[]
): Promise<{ brands: SchoolBrand[]; error: string | null }> {
  try {
    let query = supabase.from('ad_school_brands').select('*');
    if (schoolIds && schoolIds.length > 0) {
      query = query.in('school_id', schoolIds);
    }
    const { data, error } = await query.order('school_name');
    if (error) return { brands: [], error: error.message };
    return { brands: (data || []) as SchoolBrand[], error: null };
  } catch (err) {
    return { brands: [], error: err instanceof Error ? err.message : 'Failed to fetch brands' };
  }
}

export async function fetchBrandById(
  brandId: string
): Promise<{ brand: SchoolBrand | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('ad_school_brands')
      .select('*')
      .eq('id', brandId)
      .maybeSingle();
    if (error) return { brand: null, error: error.message };
    return { brand: data as SchoolBrand | null, error: null };
  } catch (err) {
    return { brand: null, error: err instanceof Error ? err.message : 'Failed to fetch brand' };
  }
}

export async function fetchBrandBySubdomain(
  subdomain: string
): Promise<{ brand: SchoolBrand | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('ad_school_brands')
      .select('*')
      .eq('subdomain', subdomain)
      .maybeSingle();
    if (error) return { brand: null, error: error.message };
    return { brand: data as SchoolBrand | null, error: null };
  } catch (err) {
    return { brand: null, error: err instanceof Error ? err.message : 'Failed to fetch brand' };
  }
}

export async function fetchAllBrands(): Promise<{ brands: SchoolBrand[]; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('ad_school_brands')
      .select('*')
      .eq('is_active', true)
      .order('school_name');
    if (error) return { brands: [], error: error.message };
    return { brands: (data || []) as SchoolBrand[], error: null };
  } catch (err) {
    return { brands: [], error: err instanceof Error ? err.message : 'Failed to fetch brands' };
  }
}

export async function fetchBrandsFiltered(filters: {
  solution?: string;
  tier?: string;
  search?: string;
  ids?: string[];
}): Promise<{ brands: SchoolBrand[]; error: string | null }> {
  try {
    let query = supabase.from('ad_school_brands').select('*').eq('is_active', true);
    if (filters.solution) query = query.eq('solution', filters.solution);
    if (filters.tier) query = query.eq('tier', filters.tier);
    if (filters.ids && filters.ids.length > 0) query = query.in('id', filters.ids);
    if (filters.search) query = query.ilike('school_name', `%${filters.search}%`);
    const { data, error } = await query.order('school_name');
    if (error) return { brands: [], error: error.message };
    return { brands: (data || []) as SchoolBrand[], error: null };
  } catch (err) {
    return { brands: [], error: err instanceof Error ? err.message : 'Failed to fetch brands' };
  }
}

export async function fetchSchoolForBrand(
  brand: SchoolBrand
): Promise<{ school: Record<string, unknown> | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('sm_schools')
      .select('*')
      .eq('id', brand.school_id)
      .maybeSingle();
    if (error) return { school: null, error: error.message };
    return { school: data, error: null };
  } catch (err) {
    return { school: null, error: err instanceof Error ? err.message : 'Failed to fetch school' };
  }
}

export function resolveElementsForBrand(
  elements: CanvasElement[],
  elementMappings: Record<string, EnhancedFieldMapping[]>,
  brand: SchoolBrand,
  sourceDataCache?: Record<string, Record<string, unknown>>
): ResolvedElement[] {
  const brandRecord = brandToRecord(brand);

  return elements.map((element) => {
    if (element.contentType === 'STATIC') {
      // Static text with a non-grow fit mode still needs the fit pass so
      // designer-time overflow (or post-font-load remeasure) shrinks on export.
      if (element.type === 'text' && element.textFitMode && element.textFitMode !== 'natural') {
        const resolved = JSON.parse(JSON.stringify(element)) as CanvasElement;
        applyTextFit(resolved as TextElement);
        return {
          original: element,
          resolved,
          appliedMappings: [],
          fullyResolved: true,
          errors: [],
        };
      }
      return {
        original: element,
        resolved: element,
        appliedMappings: [],
        fullyResolved: true,
        errors: [],
      };
    }

    const mappings = elementMappings[element.id] || [];
    if (mappings.length === 0) {
      const resolvedViaInline = resolveInlineDynamicField(element, brandRecord, sourceDataCache);
      if (resolvedViaInline) {
        if (resolvedViaInline.resolved.type === 'text') {
          applyTextFit(resolvedViaInline.resolved as TextElement);
        }
        return resolvedViaInline;
      }

      return {
        original: element,
        resolved: element,
        appliedMappings: [],
        fullyResolved: false,
        errors: ['No field mappings defined for this dynamic element'],
      };
    }

    const resolved = JSON.parse(JSON.stringify(element)) as CanvasElement;
    const appliedMappings: AppliedMapping[] = [];
    const errors: string[] = [];

    for (const mapping of mappings) {
      const { rawValue, resolvedValue, fromSource, error } = resolveMappingValue(mapping, brandRecord, sourceDataCache);
      if (error) errors.push(error);

      appliedMappings.push({ mapping, rawValue, resolvedValue, fromSource });
      applyMappedValue(resolved, mapping.targetProperty, resolvedValue);
    }

    // Single fit pass AFTER all mappings land. Eliminates mapping-order
    // dependency (textContent vs fontFamily vs fontWeight vs fontSize) and
    // guarantees the fitter sees the final state of the element.
    if (resolved.type === 'text') {
      applyTextFit(resolved as TextElement);
    }

    return {
      original: element,
      resolved,
      appliedMappings,
      fullyResolved: errors.length === 0,
      errors,
    };
  });
}

// ═══════════════════════════════════════════════════════════════════════
// Table-agnostic mapping source prefetch
// ═══════════════════════════════════════════════════════════════════════

type FetchStrategy =
  | 'brand_record'
  | 'school_scoped_single'
  | 'school_single_by_id'
  | 'brand_scoped_single'
  | 'eav_pivot';

interface SourceRule {
  table_name: string | null;
  table_prefix: string | null;
  fetch_strategy: FetchStrategy;
  join_column: string | null;
  priority: number;
}

let registryCache: SourceRule[] | null = null;

async function loadRegistry(): Promise<SourceRule[]> {
  if (registryCache) return registryCache;

  const { data, error } = await supabase
    .from('cs_mapping_source_registry')
    .select('table_name, table_prefix, fetch_strategy, join_column, priority')
    .eq('is_active', true)
    .order('priority', { ascending: true });

  if (error || !data || data.length === 0) {
    // Fallback to built-in defaults if registry is unreachable or returns no
    // rows (e.g. unauthenticated session blocked by RLS).
    registryCache = BUILTIN_REGISTRY;
    return registryCache;
  }

  registryCache = data as SourceRule[];
  return registryCache;
}

const BUILTIN_REGISTRY: SourceRule[] = [
  { table_name: 'ad_school_brands', table_prefix: null, fetch_strategy: 'brand_record', join_column: null, priority: 10 },
  { table_name: 'sm_school_attributes', table_prefix: null, fetch_strategy: 'eav_pivot', join_column: 'school_id', priority: 15 },
  { table_name: 'sm_schools', table_prefix: null, fetch_strategy: 'school_single_by_id', join_column: 'id', priority: 20 },
  { table_name: null, table_prefix: 'ad_', fetch_strategy: 'brand_scoped_single', join_column: 'school_brand_id', priority: 50 },
  { table_name: null, table_prefix: 'sm_', fetch_strategy: 'school_scoped_single', join_column: 'school_id', priority: 50 },
];

function resolveRule(tableName: string, registry: SourceRule[]): SourceRule | null {
  const exact = registry.find((r) => r.table_name === tableName);
  if (exact) return exact;
  const prefixMatch = registry
    .filter((r) => r.table_prefix && tableName.startsWith(r.table_prefix))
    .sort((a, b) => a.priority - b.priority)[0];
  return prefixMatch || null;
}

export async function fetchMappingSourceData(
  brand: SchoolBrand,
  elements: CanvasElement[],
  elementMappings: Record<string, EnhancedFieldMapping[]>
): Promise<Record<string, Record<string, unknown>>> {
  const tables = new Set<string>();

  for (const el of elements) {
    if (el.contentType !== 'DYNAMIC') continue;
    if ('dynamicField' in el && el.dynamicField?.table) {
      tables.add(el.dynamicField.table);
    }
    const mappings = elementMappings[el.id] || [];
    for (const m of mappings) {
      if (m.tableName) tables.add(m.tableName);
    }
  }

  if (tables.size === 0) return {};

  const registry = await loadRegistry();
  const cache: Record<string, Record<string, unknown>> = {};

  for (const tableName of tables) {
    const rule = resolveRule(tableName, registry);
    if (!rule || rule.fetch_strategy === 'brand_record') continue;

    if (rule.fetch_strategy === 'eav_pivot') {
      try {
        const joinCol = rule.join_column || 'school_id';
        const { data: rows } = await supabase
          .from(tableName)
          .select('category, attribute_key, attribute_value')
          .eq(joinCol, brand.school_id);

        if (rows && rows.length > 0) {
          const pivot: Record<string, unknown> = {};
          for (const row of rows as Array<{ category: string; attribute_key: string; attribute_value: unknown }>) {
            pivot[buildEavPivotKey(row.category, row.attribute_key)] = row.attribute_value;
          }
          cache[tableName] = pivot;
        }
      } catch {
        // silently skip
      }
      continue;
    }

    try {
      let query = supabase.from(tableName).select('*').limit(1);

      switch (rule.fetch_strategy) {
        case 'school_single_by_id':
          query = query.eq(rule.join_column || 'id', brand.school_id);
          break;
        case 'school_scoped_single':
          query = query.eq(rule.join_column || 'school_id', brand.school_id);
          break;
        case 'brand_scoped_single':
          query = query.eq(rule.join_column || 'school_brand_id', brand.id);
          break;
      }

      const { data } = await query.maybeSingle();
      if (data) {
        cache[tableName] = data as Record<string, unknown>;
      }
    } catch {
      // silently skip tables that fail to query
    }
  }

  return cache;
}

// Backwards-compatible alias. Preserves the historical export name but now
// returns the unified (table-agnostic) cache.
export const fetchSmDataForBrand = fetchMappingSourceData;

function brandToRecord(brand: SchoolBrand): Record<string, unknown> {
  const record: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(brand)) {
    record[key] = value;
  }

  if (brand.rgb_primary && brand.rgb_primary !== COLOR_NOT_SET) {
    record['primary_color_hex'] = brand.rgb_primary;
  }
  if (brand.rgb_secondary && brand.rgb_secondary !== COLOR_NOT_SET) {
    record['secondary_color_hex'] = brand.rgb_secondary;
  }
  if (brand.rgb_tertiary && brand.rgb_tertiary !== COLOR_NOT_SET) {
    record['tertiary_color_hex'] = brand.rgb_tertiary;
  }
  if (brand.rgb_quaternary && brand.rgb_quaternary !== COLOR_NOT_SET) {
    record['quaternary_color_hex'] = brand.rgb_quaternary;
  }

  return record;
}

function resolveMappingValue(
  mapping: EnhancedFieldMapping,
  brandRecord: Record<string, unknown>,
  sourceDataCache?: Record<string, Record<string, unknown>>
): { rawValue: string | null; resolvedValue: string; fromSource: boolean; error: string | null } {
  const tableName = mapping.tableName;
  const eavPlaceholder = isEavMapping(mapping)
    ? `{{${mapping.filterValue}}}`
    : `{{${mapping.columnName}}}`;

  let rawValue: unknown;

  if (isEavMapping(mapping)) {
    const record = sourceDataCache?.[tableName];
    if (!record) {
      return {
        rawValue: null,
        resolvedValue: mapping.fallbackValue || eavPlaceholder,
        fromSource: false,
        error: null,
      };
    }
    const pivotKey = buildEavPivotKey(mapping.filterCategory!, mapping.filterValue!);
    rawValue = record[pivotKey];
  } else if (tableName === 'ad_school_brands') {
    rawValue = brandRecord[mapping.columnName];
  } else if (sourceDataCache && sourceDataCache[tableName]) {
    rawValue = sourceDataCache[tableName][mapping.columnName];
  } else {
    return {
      rawValue: null,
      resolvedValue: mapping.fallbackValue || eavPlaceholder,
      fromSource: false,
      error: null,
    };
  }

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return {
      rawValue: null,
      resolvedValue: mapping.fallbackValue || eavPlaceholder,
      fromSource: false,
      error: null,
    };
  }

  let stringValue = String(rawValue);
  if (mapping.transformExpression) {
    stringValue = applyTransform(stringValue, mapping.transformExpression);
  }

  return {
    rawValue: stringValue,
    resolvedValue: stringValue,
    fromSource: true,
    error: null,
  };
}

function resolveInlineDynamicField(
  element: CanvasElement,
  brandRecord: Record<string, unknown>,
  sourceDataCache?: Record<string, Record<string, unknown>>
): ResolvedElement | null {
  if (!('dynamicField' in element) || !element.dynamicField) return null;
  const df = element.dynamicField;

  let value: unknown;
  if (df.table === 'ad_school_brands') {
    value = brandRecord[df.field];
  } else if (sourceDataCache && sourceDataCache[df.table]) {
    value = sourceDataCache[df.table][df.field];
  } else {
    return null;
  }

  const resolved = JSON.parse(JSON.stringify(element)) as CanvasElement;

  if (value !== null && value !== undefined) {
    let stringValue = String(value);
    if (df.transform) {
      stringValue = applyTransform(stringValue, df.transform);
    }
    applyMappedValue(resolved, df.targetProperty, stringValue);

    return {
      original: element,
      resolved,
      appliedMappings: [],
      fullyResolved: true,
      errors: [],
    };
  }

  if (df.fallbackValue) {
    applyMappedValue(resolved, df.targetProperty, df.fallbackValue);
  }

  return {
    original: element,
    resolved,
    appliedMappings: [],
    fullyResolved: value !== null && value !== undefined,
    errors: value === null || value === undefined ? [`No value for field "${df.field}" in brand data`] : [],
  };
}
